/**
 * useAnchoredPosition -- anchors a portalled panel to a trigger element.
 *
 * Floating UI resolves a position asynchronously, so a panel that mounts with
 * placeholder coordinates is painted at the viewport origin for a frame and
 * only then jumps to its trigger -- which reads as the panel flying in from the
 * top-left corner. This hook holds the panel invisible and inert until real
 * coordinates exist, and withholds the entrance animation until the same
 * moment, so the panel always appears to grow out of its trigger.
 *
 * Invisibility alone is not enough to rely on. A CSS animation outranks an
 * inline style in the cascade, so anything that animates `opacity` on the panel
 * would defeat the gate, and the panel would then be seen at whatever
 * coordinates it was holding. So the pre-anchor frame does three things rather
 * than one: it suppresses animations outright, it stays invisible and
 * click-through, and it sits at its own trigger rather than at the viewport
 * origin. A frame that escapes the gate for any reason shows the panel where it
 * belongs instead of in the corner of the screen.
 *
 * Coordinates are dropped on close, so a panel never re-opens at the position
 * its trigger used to occupy (after a scroll, a route change, or a row moving
 * in a virtualised list).
 */
import { useLayoutEffect, useMemo, useState, type CSSProperties, type RefObject } from "react";

import type { Placement } from "@floating-ui/dom";
import {
  setupAutoUpdate,
  toFloatingPlacement,
  type PopoverAlign,
  type PopoverSide,
  type PositionResult,
} from "./popoverPositioning";

/**
 * Entrance transition shared by every anchored panel. Applied only once the
 * panel is anchored -- see `AnchoredPosition.className`.
 */
export const ANCHORED_PANEL_ENTER = "animate-in fade-in-0 zoom-in-95 duration-150";

/** Either a ref to the trigger, or the trigger element itself. */
export type AnchorTarget = RefObject<HTMLElement | null> | HTMLElement | null;

export type UseAnchoredPositionOptions = {
  /** Whether the panel is mounted and should track its anchor. */
  open: boolean;
  /** The trigger the panel is anchored to. */
  anchor: AnchorTarget;
  /** The floating panel element. */
  panelRef: RefObject<HTMLElement | null>;
  side?: PopoverSide;
  align?: PopoverAlign;
  /** Keep clear of the viewport edge, in px. */
  viewportPadding?: number;
  /** Gap between trigger and panel, in px. */
  offsetDistance?: number;
};

export type AnchoredPosition = {
  x: number;
  y: number;
  placement: Placement;
  /** False until Floating UI has resolved a position against the anchor. */
  isPositioned: boolean;
  /** Fixed-position style for the panel. Spread this first, then any extras. */
  style: CSSProperties;
  /** Entrance animation classes -- empty until the panel is anchored. */
  className: string;
};

const resolveAnchor = (anchor: AnchorTarget): HTMLElement | null => {
  if (!anchor) return null;
  return anchor instanceof HTMLElement ? anchor : anchor.current;
};

/**
 * Where to park the panel for the frame or two before Floating UI has measured
 * anything. This is the trigger's own edge -- close enough that the panel is
 * never somewhere surprising, and replaced by exact coordinates (flip, shift
 * and size applied) as soon as the first compute resolves.
 *
 * The panel's own box is only known from the second render onwards, so the
 * sides that need it fall back to the trigger's edge. Being a little off is
 * fine; being at the viewport origin is not.
 */
const seedFromAnchor = (
  anchorEl: HTMLElement | null,
  panelEl: HTMLElement | null,
  side: PopoverSide,
  offsetDistance: number,
): { x: number; y: number } | null => {
  if (!anchorEl) return null;
  const rect = anchorEl.getBoundingClientRect();
  const panelWidth = panelEl?.offsetWidth ?? 0;
  const panelHeight = panelEl?.offsetHeight ?? 0;

  switch (side) {
    case "top":
      return { x: rect.left, y: rect.top - panelHeight - offsetDistance };
    case "left":
      return { x: rect.left - panelWidth - offsetDistance, y: rect.top };
    case "right":
      return { x: rect.right + offsetDistance, y: rect.top };
    default:
      return { x: rect.left, y: rect.bottom + offsetDistance };
  }
};

export const useAnchoredPosition = ({
  open,
  anchor,
  panelRef,
  side = "bottom",
  align = "start",
  viewportPadding = 12,
  offsetDistance = 4,
}: UseAnchoredPositionOptions): AnchoredPosition => {
  const [position, setPosition] = useState<PositionResult | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }

    const anchorEl = resolveAnchor(anchor);
    const panelEl = panelRef.current;
    if (!anchorEl || !panelEl) return;

    // The initial compute resolves on a microtask, which can land after the
    // panel has already closed; ignore anything that arrives after teardown.
    let cancelled = false;

    const cleanup = setupAutoUpdate(
      anchorEl,
      panelEl,
      { side, align, viewportPadding, offsetDistance },
      (result) => {
        if (!cancelled) setPosition(result);
      },
    );

    return () => {
      cancelled = true;
      cleanup();
      setPosition(null);
    };
  }, [open, anchor, panelRef, side, align, viewportPadding, offsetDistance]);

  return useMemo(() => {
    if (!position) {
      const seed = open
        ? seedFromAnchor(resolveAnchor(anchor), panelRef.current, side, offsetDistance)
        : null;

      return {
        x: seed?.x ?? 0,
        y: seed?.y ?? 0,
        placement: toFloatingPlacement(side, align),
        isPositioned: false,
        // Kept in the layout (Floating UI has to measure it) but invisible and
        // click-through, so the pre-anchor frame is never seen or hit. Opacity
        // rather than `visibility` keeps the panel focusable, so focus moved
        // into it on open is never dropped -- a filter dropdown focuses its
        // search box the moment it opens.
        //
        // `animation: none` is what makes the invisibility dependable: an
        // animation that touched `opacity` would outrank the inline value and
        // reveal the panel. Nothing can animate this frame. The entrance
        // animation is added back below, once there are real coordinates to
        // play it against.
        style: {
          position: "fixed",
          top: seed?.y ?? 0,
          left: seed?.x ?? 0,
          opacity: 0,
          pointerEvents: "none",
          animation: "none",
        },
        className: "",
      };
    }

    return {
      x: position.x,
      y: position.y,
      placement: position.placement,
      isPositioned: true,
      style: { position: "fixed", top: position.y, left: position.x },
      className: ANCHORED_PANEL_ENTER,
    };
  }, [position, side, align, open, anchor, panelRef, offsetDistance]);
};
